import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Play } from 'lucide-react';
import { useAuth } from '../../../auth';
import { useTemplate } from '../../../portal/hooks/useTemplates';
import { PRIMITIVES, DEFAULT_INPUTS, API_BASE } from '../constants';
import EmbedCodeModal from './EmbedCodeModal';
import MagicValuesPanel from './MagicValuesPanel';
import PrimitiveSidebar from './PrimitiveSidebar';
import { TemplateBanner, TemplateResults } from './TemplateBanner';
import { useDebounce } from '../shared/useDebounce';
import { getModifierKey } from '../shared/useKeyboardShortcuts';

export default function ExplorerTab() {
  const { getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const templateId = searchParams.get('template');
  const { data: template, isLoading: templateLoading } = useTemplate(templateId);

  const [selectedPrimitive, setSelectedPrimitive] = useState('screening.individual');
  const [requestBody, setRequestBody] = useState(JSON.stringify(DEFAULT_INPUTS['screening.individual'], null, 2));
  const [response, setResponse] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executionTime, setExecutionTime] = useState(null);
  const [error, setError] = useState(null);
  const [showMagicValues, setShowMagicValues] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedLanguage, setEmbedLanguage] = useState('curl');
  const [templateResults, setTemplateResults] = useState(null);
  const [runningTemplate, setRunningTemplate] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(null);

  // Ref for triggering run from keyboard shortcut
  const handleRunRef = useRef(null);
  const modKey = getModifierKey();

  // Listen for keyboard shortcut events from parent
  useEffect(() => {
    const handleRunEvent = () => {
      if (handleRunRef.current && !loading) {
        handleRunRef.current();
      }
    };

    const handleToggleMagicEvent = () => {
      setShowMagicValues(prev => !prev);
    };

    window.addEventListener('runWorkbenchRequest', handleRunEvent);
    window.addEventListener('toggleMagicValues', handleToggleMagicEvent);

    return () => {
      window.removeEventListener('runWorkbenchRequest', handleRunEvent);
      window.removeEventListener('toggleMagicValues', handleToggleMagicEvent);
    };
  }, [loading]);

  // When template changes, load first step
  useEffect(() => {
    if (template?.steps?.length > 0) {
      const firstStep = template.steps[0];
      setSelectedPrimitive(firstStep.primitive_id);
      setRequestBody(JSON.stringify(firstStep.sample_input, null, 2));
      setTemplateResults(null);
      setResponse(null);
      setStatus(null);
    }
  }, [template]);

  function handleSelectPrimitive(id) {
    setSelectedPrimitive(id);
    if (template) {
      const step = template.steps?.find(s => s.primitive_id === id);
      if (step) {
        setRequestBody(JSON.stringify(step.sample_input, null, 2));
      } else {
        setRequestBody(JSON.stringify(DEFAULT_INPUTS[id] || {}, null, 2));
      }
    } else {
      setRequestBody(JSON.stringify(DEFAULT_INPUTS[id] || {}, null, 2));
    }
    setResponse(null);
    setStatus(null);
    setExecutionTime(null);
    setError(null);
  }

  function handleExitTemplate() {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('template');
    setSearchParams(newParams);
    setTemplateResults(null);
    setResponse(null);
    setStatus(null);
  }

  async function handleRun() {
    setLoading(true);
    setResponse(null);
    setStatus(null);
    setExecutionTime(null);
    setError(null);

    let parsed;
    try {
      parsed = JSON.parse(requestBody);
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
      setLoading(false);
      return;
    }

    const startTime = Date.now();

    try {
      const token = await getToken();
      const payload = { inputs: parsed };
      const res = await fetch(`${API_BASE}/primitives/${selectedPrimitive}/invoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const elapsed = Date.now() - startTime;
      setExecutionTime(elapsed);
      setStatus(res.status);
      const data = await res.json();
      setResponse(data);
    } catch (e) {
      const elapsed = Date.now() - startTime;
      setExecutionTime(elapsed);
      setStatus(500);
      setResponse({ error: e.message });
    } finally {
      setLoading(false);
    }
  }

  // Debounce Run button to prevent double-click spam
  const debouncedRun = useDebounce(handleRun, 300);

  // Keep ref updated for keyboard shortcut
  useEffect(() => {
    handleRunRef.current = debouncedRun;
  }, [debouncedRun]);

  async function handleRunAllSteps() {
    if (!template) return;

    setRunningTemplate(true);
    setTemplateResults(null);
    setResponse(null);
    setError(null);

    const results = [];
    const token = await getToken();

    for (let i = 0; i < template.steps.length; i++) {
      const step = template.steps[i];
      setCurrentStepIndex(i);
      setSelectedPrimitive(step.primitive_id);
      setRequestBody(JSON.stringify(step.sample_input, null, 2));

      const startTime = Date.now();

      try {
        const res = await fetch(`${API_BASE}/primitives/${step.primitive_id}/invoke`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ inputs: step.sample_input }),
        });

        const elapsed = Date.now() - startTime;
        const data = await res.json();

        results.push({
          step: i + 1,
          name: step.name,
          primitive_id: step.primitive_id,
          input: step.sample_input,
          output: data,
          execution_time_ms: elapsed,
          passed: res.ok,
        });
      } catch (e) {
        const elapsed = Date.now() - startTime;
        results.push({
          step: i + 1,
          name: step.name,
          primitive_id: step.primitive_id,
          input: step.sample_input,
          output: { error: e.message },
          execution_time_ms: elapsed,
          passed: false,
        });
      }
    }

    setCurrentStepIndex(null);
    setTemplateResults(results);
    setRunningTemplate(false);
  }

  function generateEmbedCode(lang) {
    const apiKey = 'YOUR_API_KEY';
    const baseUrl = API_BASE;

    if (lang === 'curl') {
      return `curl -X POST "${baseUrl}/primitives/${selectedPrimitive}/invoke" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '${requestBody}'`;
    }

    if (lang === 'javascript') {
      return `const response = await fetch('${baseUrl}/primitives/${selectedPrimitive}/invoke', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${API_KEY}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(${requestBody}),
});

const data = await response.json();
console.log(data);`;
    }

    if (lang === 'python') {
      return `import requests

response = requests.post(
    '${baseUrl}/primitives/${selectedPrimitive}/invoke',
    headers={
        'Authorization': f'Bearer {API_KEY}',
        'Content-Type': 'application/json',
    },
    json=${requestBody.replace(/"/g, "'")}
)

print(response.json())`;
    }

    return '';
  }

  function handleSelectTemplateResult(result) {
    setSelectedPrimitive(result.primitive_id);
    setRequestBody(JSON.stringify(result.input, null, 2));
    setResponse(result.output);
    setStatus(result.passed ? 200 : 422);
    setExecutionTime(result.execution_time_ms);
  }

  const selectedLabel = PRIMITIVES.find(p => p.id === selectedPrimitive)?.label || '';

  if (templateId && templateLoading) {
    return (
      <div className="workbench-loading">
        <div className="workbench-spinner" />
      </div>
    );
  }

  return (
    <div className="explorer-container">
      {/* Template Banner */}
      {template && (
        <TemplateBanner
          template={template}
          runningTemplate={runningTemplate}
          currentStepIndex={currentStepIndex}
          onRunAllSteps={handleRunAllSteps}
          onShowEmbedModal={() => setShowEmbedModal(true)}
          onExitTemplate={handleExitTemplate}
        />
      )}

      {/* Template Results */}
      {templateResults && (
        <TemplateResults
          templateResults={templateResults}
          onSelectResult={handleSelectTemplateResult}
        />
      )}

      <div className="explorer-header">
        <div>
          <h2>API Explorer</h2>
          <p>Test primitives with magic test data. All calls are free in test mode.</p>
        </div>
        <button
          className="workbench-btn workbench-btn-ghost"
          onClick={() => setShowMagicValues(!showMagicValues)}
          aria-label={`${showMagicValues ? 'Hide' : 'Show'} magic values panel (M)`}
          aria-expanded={showMagicValues}
        >
          {showMagicValues ? 'Hide' : 'Show'} Magic Values
        </button>
      </div>

      {/* Magic Values Reference */}
      {showMagicValues && <MagicValuesPanel />}

      <div className="explorer-layout">
        <PrimitiveSidebar
          template={template}
          templateResults={templateResults}
          selectedPrimitive={selectedPrimitive}
          onSelectPrimitive={handleSelectPrimitive}
        />

        <div className="explorer-panel">
          <div className="panel-header">
            <h3>Request</h3>
            <span className="primitive-name">{selectedLabel}</span>
          </div>
          <div className="panel-body">
            <textarea
              className="workbench-code-textarea"
              value={requestBody}
              onChange={e => setRequestBody(e.target.value)}
              spellCheck={false}
              aria-label={`Request body JSON for ${selectedLabel}`}
            />
            {error && <div className="workbench-error" role="alert">{error}</div>}
            <div className="panel-actions">
              <button
                className="workbench-btn workbench-btn-primary"
                onClick={debouncedRun}
                disabled={loading}
                aria-label={`Run API request (${modKey}+Enter)`}
                aria-busy={loading}
              >
                <Play size={14} aria-hidden="true" />
                {loading ? 'Running...' : 'Run'}
              </button>
              <button
                className="workbench-btn workbench-btn-secondary"
                onClick={() => setShowEmbedModal(true)}
                aria-label="Get embed code for this request"
              >
                Get Code
              </button>
            </div>
          </div>
        </div>

        <div className="explorer-panel">
          <div className="panel-header">
            <h3>Response</h3>
            {status && (
              <span className={`workbench-badge ${status >= 200 && status < 300 ? 'workbench-badge-success' : 'workbench-badge-error'}`}>
                {status}
              </span>
            )}
          </div>
          <div className="panel-body">
            {response ? (
              <>
                <pre className="workbench-code-block">{JSON.stringify(response, null, 2)}</pre>
                {executionTime !== null && (
                  <div className="response-meta">
                    <span>Execution time: {executionTime}ms</span>
                  </div>
                )}
              </>
            ) : (
              <div className="response-placeholder">
                {loading ? 'Executing...' : 'Select a primitive and click Run to see the response.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Embed Code Modal */}
      {showEmbedModal && (
        <EmbedCodeModal
          selectedLabel={selectedLabel}
          embedLanguage={embedLanguage}
          setEmbedLanguage={setEmbedLanguage}
          generateEmbedCode={generateEmbedCode}
          onClose={() => setShowEmbedModal(false)}
        />
      )}
    </div>
  );
}
